import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUIStore } from '@/stores/uiStore';
import type { Project, ProjectFormData, ProjectStatus } from '@/types';

const projectSchema = z.object({
  name: z.string().min(1, '프로젝트 이름은 필수입니다').max(100),
  description: z.string().max(500).optional(),
  status: z.enum(['active', 'on_hold', 'completed', 'deleted']).optional(),
});

const statusOptions: { value: ProjectStatus; label: string; description: string }[] = [
  { value: 'active', label: '진행중', description: '현재 작업 중인 프로젝트' },
  { value: 'on_hold', label: '보류', description: '일시적으로 중단된 프로젝트' },
  { value: 'completed', label: '완료', description: '모든 작업이 완료된 프로젝트' },
  { value: 'deleted', label: '삭제', description: '더 이상 사용하지 않는 프로젝트' },
];

interface ProjectSettingsModalProps {
  project: Project | null;
  onSave: (data: Partial<ProjectFormData>) => Promise<Project | void>;
  isUpdating?: boolean;
}

export function ProjectSettingsModal({
  project,
  onSave,
  isUpdating = false,
}: ProjectSettingsModalProps) {
  const { modals, closeModal } = useUIStore();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: project?.name || '',
      description: project?.description || '',
      status: project?.status || 'active',
    },
  });

  const watchedStatus = watch('status');

  // Update form when project changes
  useEffect(() => {
    if (project && modals.projectSettings) {
      reset({
        name: project.name,
        description: project.description || '',
        status: project.status,
      });
    }
  }, [project, modals.projectSettings, reset]);

  const onSubmit = async (data: ProjectFormData) => {
    setError(null);
    try {
      await onSave(data);
      closeModal('projectSettings');
    } catch (err) {
      setError(err instanceof Error ? err.message : '프로젝트 수정에 실패했습니다');
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    closeModal('projectSettings');
  };

  return (
    <Dialog open={modals.projectSettings} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>프로젝트 설정</DialogTitle>
          <DialogDescription>
            프로젝트의 정보와 상태를 수정합니다.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="settings-name">프로젝트 이름</Label>
            <Input
              id="settings-name"
              placeholder="나의 멋진 프로젝트"
              error={!!errors.name}
              {...register('name')}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="settings-description">설명 (선택사항)</Label>
            <Textarea
              id="settings-description"
              placeholder="프로젝트에 대한 간단한 설명..."
              rows={3}
              error={!!errors.description}
              {...register('description')}
            />
            {errors.description && (
              <p className="text-xs text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="settings-status">프로젝트 상태</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger id="settings-status">
                    <SelectValue placeholder="상태 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {watchedStatus === 'deleted' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                삭제된 프로젝트는 대시보드에서 숨겨지며, 복구하려면 상태를 다시 변경해야 합니다.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              취소
            </Button>
            <Button
              type="submit"
              loading={isUpdating}
              disabled={!isDirty}
              variant={watchedStatus === 'deleted' ? 'destructive' : 'default'}
            >
              저장
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
